import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  FileText,
  Globe,
  Search,
  TrendingUp,
} from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  fetchCheckRuns,
  fetchComplianceSummary,
  fetchFindings,
  fetchMetrics,
  fetchProducts,
  fetchTermWatches,
  type CheckRunListDto,
} from '../services/api'
import {
  EmptyState,
  KpiCard,
  MimCard,
  MimPage,
  StatusPill,
  formatDateTime,
  relativeAge,
} from '../components/MimUi'

export default function Dashboard() {
  const { data: metrics, isLoading: metricsLoading } = useQuery({ queryKey: ['metrics'], queryFn: fetchMetrics })
  const { data: products = [] } = useQuery({ queryKey: ['products', 'dashboard'], queryFn: () => fetchProducts({ kingspanOnly: true }) })
  const { data: runs = [] } = useQuery({ queryKey: ['check-runs', 'dashboard'], queryFn: () => fetchCheckRuns({ take: 12 }) })
  const { data: findingsPage } = useQuery({ queryKey: ['findings', 'dashboard'], queryFn: () => fetchFindings({ status: 'Open' }) })
  const findings = findingsPage?.items ?? []
  const { data: summary } = useQuery({ queryKey: ['compliance-summary-dashboard'], queryFn: () => fetchComplianceSummary() })
  const { data: watches = [] } = useQuery({ queryKey: ['term-watches', 'dashboard'], queryFn: fetchTermWatches })

  const productCount = products.length
  const sitesChecked = summary?.sitesChecked ?? runs.reduce((sum, run) => sum + (run.targetCount || 0), 0)
  const openExceptions = findings.length || metrics?.openFindings || 0
  const complianceRate = metrics ? Math.round(metrics.complianceRate * 100) : 0

  const trendData = useMemo(() => buildRunTrend(runs), [runs])
  const exceptionTrend = useMemo(() => runs.slice(0, 6).reverse().map((run, index) => ({
    label: `Run ${index + 1}`,
    exceptions: run.findingCount || 0,
  })), [runs])

  const divisionStats = useMemo(() => {
    const map = new Map<string, { total: number; exceptions: number }>()
    for (const product of products) {
      const key = product.division || 'Unassigned'
      const current = map.get(key) ?? { total: 0, exceptions: 0 }
      current.total += 1
      map.set(key, current)
    }
    for (const finding of findings) {
      const key = products.find((p) => finding.description?.includes(p.code) || finding.description?.includes(p.name))?.division || 'Unassigned'
      const current = map.get(key) ?? { total: 0, exceptions: 0 }
      current.exceptions += 1
      map.set(key, current)
    }
    return Array.from(map.entries()).slice(0, 5).map(([name, values]) => ({
      name,
      total: values.total,
      compliant: Math.max(0, values.total - values.exceptions),
      pct: values.total > 0 ? Math.max(0, Math.round(((values.total - values.exceptions) / values.total) * 100)) : 0,
    }))
  }, [products, findings])

  if (metricsLoading) {
    return (
      <MimPage title="Dashboard" subtitle="Compliance overview" compactHeader>
        <EmptyState title="Loading dashboard" detail="Fetching compliance metrics..." />
      </MimPage>
    )
  }

  return (
    <MimPage title="Dashboard" subtitle="Compliance overview" compactHeader>
      <div className="mim-stack">
        <div className="mim-grid-4">
          <KpiCard label="Products Registered" value={productCount} detail="Source-of-truth products" icon={<FileText size={20} />} />
          <KpiCard label="Sites Checked" value={sitesChecked} detail="Across recent check runs" icon={<Globe size={20} />} />
          <KpiCard label="Open Exceptions" value={openExceptions} detail={`${metrics?.overdueActions ?? 0} overdue actions`} tone="gold" icon={<AlertTriangle size={20} />} />
          <KpiCard label="Compliance Rate" value={`${complianceRate}%`} detail="Based on completed checks" tone="green" icon={<CheckCircle2 size={20} />} />
        </div>

        <div className="mim-grid-3">
          <MimCard title="Monthly Compliance Rate" subtitle="Pass vs fail by recent check runs" className="dashboard-wide-card">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9edf2" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="pass" name="Pass" fill="var(--mim-blue)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="fail" name="Fail" fill="var(--mim-gold)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </MimCard>

          <MimCard title="Exception Trend">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={exceptionTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9edf2" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="exceptions" stroke="var(--mim-gold)" strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </MimCard>
        </div>

        <div className="mim-grid-3">
          <MimCard
            title="Recent Compliance Checks"
            actions={<Link to="/check-history" className="mim-btn mim-btn-secondary">View all <ArrowRight size={13} /></Link>}
            className="dashboard-wide-card"
          >
            {runs.length === 0 ? (
              <EmptyState title="No recent checks" detail="Run a website check to populate this table." />
            ) : (
              <div className="mim-table-wrap">
                <table className="mim-table">
                  <thead>
                    <tr>
                      <th>Website</th>
                      <th>Status</th>
                      <th>Targets</th>
                      <th>Findings</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.slice(0, 6).map((run) => (
                      <tr key={run.id}>
                        <td>
                          <strong>{run.rootUrl || run.reason || 'Compliance check'}</strong>
                          <div className="mim-mono">{run.id.slice(0, 8)}</div>
                        </td>
                        <td><StatusPill status={run.status} /></td>
                        <td>{run.targetCount}</td>
                        <td>{run.findingCount}</td>
                        <td>{formatDateTime(run.startedUtc)}</td>
                        <td><Link to={`/exception-report?checkRunId=${run.id}`}>View</Link></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </MimCard>

          <div className="mim-stack">
            <MimCard title="Division Compliance">
              {divisionStats.length === 0 ? (
                <EmptyState title="No division data" />
              ) : (
                <div className="mim-stack">
                  {divisionStats.map((division) => (
                    <div key={division.name}>
                      <div className="dashboard-progress-head">
                        <strong>{division.name}</strong>
                        <span>{division.pct}%</span>
                      </div>
                      <div className="mim-progress"><span style={{ width: `${division.pct}%` }} /></div>
                      <div className="dashboard-progress-sub">{division.compliant}/{division.total} products compliant</div>
                    </div>
                  ))}
                </div>
              )}
            </MimCard>

            <MimCard
              title="Term Watcher Alerts"
              actions={<Link to="/term-watcher" className="mim-btn mim-btn-secondary">Manage <ArrowRight size={13} /></Link>}
            >
              {watches.length === 0 ? (
                <EmptyState title="No watched terms" />
              ) : (
                <div className="mim-stack">
                  {watches.slice(0, 5).map((watch) => (
                    <div className="dashboard-alert-row" key={watch.id}>
                      <Search size={15} />
                      <div>
                        <strong>"{watch.term}"</strong>
                        <span>{watch.lastScanUtc ? `Last scan ${relativeAge(watch.lastScanUtc)}` : 'No scan yet'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </MimCard>

            <MimCard title="Quick Actions">
              <div className="mim-stack">
                <Link className="mim-btn mim-btn-primary" to="/pcr-upload"><FileText size={14} /> Upload New PCR</Link>
                <Link className="mim-btn mim-btn-gold" to="/compliance-check"><Globe size={14} /> Run Website Check</Link>
                <Link className="mim-btn mim-btn-secondary" to="/exception-report"><TrendingUp size={14} /> View Exception Report</Link>
              </div>
            </MimCard>
          </div>
        </div>
      </div>
    </MimPage>
  )
}

function buildRunTrend(runs: CheckRunListDto[]) {
  const recent = runs.slice(0, 6).reverse()
  if (recent.length === 0) {
    return [
      { label: 'No data', pass: 0, fail: 0 },
    ]
  }
  return recent.map((run, index) => {
    const total = Math.max(1, run.processedCount || run.targetCount || 1)
    const fail = Math.min(total, run.findingCount || 0)
    const pass = Math.max(0, total - fail)
    return {
      label: `Run ${index + 1}`,
      pass,
      fail,
    }
  })
}
